/*
  Copyright (c) 2018-present, Accuracy.
  All rights reserved.
*/
import {createStyles, StyleRules, Theme} from "@material-ui/core";
import makeStyles from '@material-ui/core/styles/makeStyles';
import classNames from 'classnames';
import React, {memo} from 'react';
import {useTranslation} from "react-i18next";
import {asset} from "../../utils/asset";

//----------------------------------------------------------------------------------------------------------------------
//- Styles
//----------------------------------------------------------------------------------------------------------------------
export type AboutClassKey = 'root' | 'left' | 'picture' | 'right' | 'heading' | 'paragraph';

const useStyles = makeStyles<Theme, {}, AboutClassKey>(
  (): StyleRules<AboutClassKey> =>
    createStyles({
      root: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(20px, 3vw, 44px)',
        alignItems: 'flex-start',
        maxWidth: '1100px',
        margin: '0 auto',
        padding: 'clamp(48px, 10vh, 120px) clamp(20px, 4vw, 56px) clamp(60px, 10vh, 120px)'
      },
      left: {
        flex: '1 1 280px',
        maxWidth: '380px',
        position: 'sticky',
        top: '100px',
        // Below ~900px the two columns stack into full-width rows (root is a
        // wrapping flex row). A sticky element only stays pinned within its
        // containing block, which here spans the whole section — so once
        // stacked, keeping position:sticky would pin the photo in place while
        // the paragraphs beneath it scroll up and under it. Falling back to
        // static keeps it a normal, non-overlapping block on mobile.
        '@media (max-width: 900px)': {
          position: 'static',
          maxWidth: '480px',
          margin: '0 auto'
        }
      },
      picture: {
        display: 'block',
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
        background: 'var(--color-surface)',
        filter: 'grayscale(1) contrast(1.05)'
      },
      right: {
        flex: '2 1 420px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      },
      heading: {
        margin: 0,
        fontFamily: 'var(--font-display)',
        fontSize: 'clamp(30px, 4vw, 48px)',
        lineHeight: 1.1,
        letterSpacing: '-.015em'
      },
      paragraph: {
        margin: 0,
        maxWidth: '62ch',
        fontSize: '15px',
        lineHeight: 1.85,
        color: 'var(--color-text-body)'
      }
    }),
  {
    name: 'About'
  });

//----------------------------------------------------------------------------------------------------------------------
//- Component
//----------------------------------------------------------------------------------------------------------------------
export type AboutProps = {
  className?: string;
};

export default memo(({className}: AboutProps) => {
  // hooks
  const classes = useStyles({});
  const {t} = useTranslation('common');

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.left}>
        <img className={classes.picture} src={asset('files/img/pierre.jpg')} alt="Pierre Castay"/>
      </div>
      <div className={classes.right}>
        <h2 className={classes.heading}>{t('topBarMenu.aboutMe')}</h2>
        <p className={classes.paragraph}>{t('about.p1')}</p>
        <p className={classes.paragraph}>{t('about.p2')}</p>
        <p className={classes.paragraph}>{t('about.p3')}</p>
        <p className={classes.paragraph}>{t('about.p4')}</p>
        <p className={classes.paragraph}>{t('about.p5')}</p>
        <p className={classes.paragraph}>{t('about.p6')}</p>
        <p className={classes.paragraph}>{t('about.p7')}</p>
        <p className={classes.paragraph}>{t('about.thanks')}</p>
      </div>
    </div>
  );
});
